import { useState, useEffect } from 'react'
import { supabase, auth, db } from '../../lib/supabase'

export default function Usuario() {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  
  const [formData, setFormData] = useState({
    nombre: '',
    email: ''
  })
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Superadmin state
  const [allUsers, setAllUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newUserData, setNewUserData] = useState({
    nombre: '',
    email: '',
    password: '',
    tipo: 'Marketing',
    es_superadmin: false
  })
  const [editUserData, setEditUserData] = useState({
    tipo: 'Marketing',
    es_superadmin: false
  })
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  })

  const tiposUsuario = ['Admin', 'Marketing', 'Comercial', 'Legal', 'TI']

  useEffect(() => {
    fetchUserData()
  }, [])

  useEffect(() => {
    if (userData?.es_superadmin) {
      fetchAllUsers()
    }
  }, [userData])

  const fetchUserData = async () => {
    try {
      setLoading(true)
      
      const { session } = await auth.getSession()
      if (!session) return

      setUser(session.user)

      const { data, error } = await supabase
        .from('usuarios_marketing')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .single()

      if (error) throw error

      setUserData(data)
      setFormData({
        nombre: data.nombre || '',
        email: data.email || ''
      })
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true)
      const { data, error } = await db.getAllUsuarios()
      
      if (error) throw error
      
      setAllUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const { error } = await supabase
        .from('usuarios_marketing')
        .update({
          nombre: formData.nombre
        })
        .eq('id', userData.id)

      if (error) throw error

      setMessage({ type: 'success', text: 'Perfil actualizado exitosamente' })
      fetchUserData()
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: 'Error al actualizar el perfil' })
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setMessage({ type: '', text: '' })

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' })
      return
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' })
      return
    }

    setSaving(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      })

      if (error) throw error

      setMessage({ type: 'success', text: 'Contraseña actualizada exitosamente' })
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: 'Error al cambiar la contraseña' })
    } finally {
      setSaving(false)
    }
  }

  // Superadmin functions
  const handleCreateUser = async (e) => {
    e.preventDefault()
    
    if (newUserData.password.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' })
      return
    }

    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const { data, error } = await auth.createUser(
        newUserData.email,
        newUserData.password,
        newUserData.nombre,
        newUserData.tipo
      )

      if (error) throw error

      // Update es_superadmin if needed
      if (newUserData.es_superadmin && data.user) {
        await db.updateUsuario(data.user.id, { es_superadmin: true })
      }

      setMessage({ type: 'success', text: 'Usuario creado exitosamente' })
      setShowCreateModal(false)
      setNewUserData({
        nombre: '',
        email: '',
        password: '',
        tipo: 'Marketing',
        es_superadmin: false
      })
      fetchAllUsers()
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: error.message || 'Error al crear usuario' })
    } finally {
      setSaving(false)
    }
  }

  const handleEditUser = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const { error } = await db.updateUsuario(selectedUser.id, {
        tipo: editUserData.tipo,
        es_superadmin: editUserData.es_superadmin
      })

      if (error) throw error

      setMessage({ type: 'success', text: 'Usuario actualizado exitosamente' })
      setShowEditModal(false)
      setSelectedUser(null)
      fetchAllUsers()
      
      // Refresh own data if editing self
      if (selectedUser.auth_user_id === user.id) {
        fetchUserData()
      }
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: 'Error al actualizar usuario' })
    } finally {
      setSaving(false)
    }
  }

  const handleResetUserPassword = async (e) => {
    e.preventDefault()

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' })
      return
    }

    if (resetPasswordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' })
      return
    }

    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const { data, error } = await auth.updateUserPassword(
        selectedUser.auth_user_id,
        resetPasswordData.newPassword
      )

      if (error) throw error

      setMessage({ type: 'success', text: 'Contraseña reseteada exitosamente' })
      setShowPasswordModal(false)
      setSelectedUser(null)
      setResetPasswordData({ newPassword: '', confirmPassword: '' })
    } catch (error) {
      console.error('Error:', error)
      setMessage({ 
        type: 'error', 
        text: error.message || 'Error al resetear contraseña. Asegúrate de que el Edge Function esté desplegado.' 
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUser = async (user) => {
    if (!confirm(`¿Estás seguro de eliminar al usuario ${user.nombre}? Esta acción no se puede deshacer.`)) {
      return
    }

    setSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const { error } = await db.deleteUsuario(user.auth_user_id)

      if (error) throw error

      setMessage({ type: 'success', text: 'Usuario eliminado exitosamente' })
      fetchAllUsers()
    } catch (error) {
      console.error('Error:', error)
      setMessage({ type: 'error', text: 'Error al eliminar usuario' })
    } finally {
      setSaving(false)
    }
  }

  const openEditModal = (user) => {
    setSelectedUser(user)
    setEditUserData({
      tipo: user.tipo,
      es_superadmin: user.es_superadmin
    })
    setShowEditModal(true)
  }

  const openPasswordModal = (user) => {
    setSelectedUser(user)
    setResetPasswordData({ newPassword: '', confirmPassword: '' })
    setShowPasswordModal(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">MI PERFIL</h1>
      
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 bg-primary text-white rounded-full flex items-center justify-center text-3xl font-bold mb-4">
              {userData?.nombre?.charAt(0).toUpperCase()}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{userData?.nombre}</h2>
            <p className="text-gray-600">{userData?.email}</p>
            {userData?.cuenta_activada && (
              <span className="mt-2 px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                ● Cuenta Activa
              </span>
            )}
          </div>

          <div className="mt-6 space-y-3 border-t pt-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Fecha de Registro:</span>
              <span className="font-medium">{new Date(userData?.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Rol:</span>
              <span className="font-medium text-primary">
                {userData.tipo}
                {userData.es_superadmin && (
                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-bold">
                    ⭐ SUPERADMIN
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Estado:</span>
              <span className="font-medium text-green-600">
                {userData?.cuenta_activada ? 'Activa' : 'Pendiente'}
              </span>
            </div>
          </div>
        </div>

        {/* Edit Profile Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">INFORMACIÓN PERSONAL</h2>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">El email no se puede modificar</p>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </form>
          </div>

          {/* Change Password Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">CAMBIAR CONTRASEÑA</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Cambiando...' : 'Cambiar Contraseña'}
              </button>
            </form>
          </div>

          {/* Security Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-blue-900 mb-3">
              🔒 CONSEJOS DE SEGURIDAD
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• Usa una contraseña segura y única</li>
              <li>• Cambia tu contraseña regularmente</li>
              <li>• No compartas tus credenciales</li>
              <li>• Cierra sesión al terminar</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Superadmin Section */}
      {userData?.es_superadmin && (
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-md">
            <div className="border-b p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary text-white rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
                    ⭐
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h2>
                    <p className="text-sm text-gray-600">Administración avanzada del sistema</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2 justify-center sm:justify-start whitespace-nowrap"
                >
                  <span className="text-xl">+</span>
                  Crear Usuario
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="p-6">
              {loadingUsers ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-gray-600">Cargando usuarios...</p>
                </div>
              ) : allUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No hay usuarios registrados
                </div>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {allUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                                {u.nombre.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900">{u.nombre}</div>
                                {u.es_superadmin && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                                    ⭐ Superadmin
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {u.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                              {u.tipo}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                              u.cuenta_activada 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {u.cuenta_activada ? '● Activa' : '○ Pendiente'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditModal(u)}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                                title="Editar usuario"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => openPasswordModal(u)}
                                className="text-orange-600 hover:text-orange-800 font-medium"
                                title="Resetear contraseña"
                              >
                                🔑
                              </button>
                              {u.auth_user_id !== user.id && (
                                <button
                                  onClick={() => handleDeleteUser(u)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                  title="Eliminar usuario"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Crear Nuevo Usuario</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre Completo</label>
                <input
                  type="text"
                  value={newUserData.nombre}
                  onChange={(e) => setNewUserData({ ...newUserData, nombre: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña Inicial</label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Usuario</label>
                <select
                  value={newUserData.tipo}
                  onChange={(e) => setNewUserData({ ...newUserData, tipo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  {tiposUsuario.map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="newUserSuperadmin"
                  checked={newUserData.es_superadmin}
                  onChange={(e) => setNewUserData({ ...newUserData, es_superadmin: e.target.checked })}
                  className="w-4 h-4 text-primary focus:ring-primary rounded"
                />
                <label htmlFor="newUserSuperadmin" className="text-sm text-gray-700">
                  ⭐ Asignar privilegios de Superadmin
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setNewUserData({ nombre: '', email: '', password: '', tipo: 'Marketing', es_superadmin: false })
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Creando...' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Editar Usuario: {selectedUser.nombre}</h3>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Usuario</label>
                <select
                  value={editUserData.tipo}
                  onChange={(e) => setEditUserData({ ...editUserData, tipo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  {tiposUsuario.map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="editUserSuperadmin"
                  checked={editUserData.es_superadmin}
                  onChange={(e) => setEditUserData({ ...editUserData, es_superadmin: e.target.checked })}
                  className="w-4 h-4 text-primary focus:ring-primary rounded"
                />
                <label htmlFor="editUserSuperadmin" className="text-sm text-gray-700">
                  ⭐ Privilegios de Superadmin
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedUser(null)
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Resetear Contraseña: {selectedUser.nombre}</h3>
            <form onSubmit={handleResetUserPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
                <input
                  type="password"
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Contraseña</label>
                <input
                  type="password"
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false)
                    setSelectedUser(null)
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Reseteando...' : 'Resetear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
